"use client"

import { useEditor, EditorContent, Extension } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Color } from '@tiptap/extension-color'
import { TextStyle } from '@tiptap/extension-text-style'
import { FontFamily } from '@tiptap/extension-font-family'
import { Underline } from '@tiptap/extension-underline'
import { TextAlign } from '@tiptap/extension-text-align'
import { Bold, Italic, Underline as UnderlineIcon, AlignLeft, AlignCenter, AlignRight, Palette } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { FontLoader } from './FontLoader'

// Custom FontSize extension
export const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return {
      types: ['textStyle'],
    }
  },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: element => element.style.fontSize.replace('px', ''),
          renderHTML: attributes => {
            if (!attributes.fontSize) return {}
            return {
              style: `font-size: ${attributes.fontSize}px`,
            }
          },
        },
      },
    }]
  },
  addCommands() {
    return {
      setFontSize: (fontSize: string) => ({ chain }: any) => {
        return chain()
          .setMark('textStyle', { fontSize })
          .run()
      },
      unsetFontSize: () => ({ chain }: any) => {
        return chain()
          .setMark('textStyle', { fontSize: null })
          .removeEmptyTextStyle()
          .run()
      },
    }
  },
})

const FONT_FAMILIES = [
  // Popular Sans-Serif
  { name: 'Inter', value: 'Inter, sans-serif' },
  { name: 'Roboto', value: 'Roboto, sans-serif' },
  { name: 'Open Sans', value: '"Open Sans", sans-serif' },
  { name: 'Lato', value: 'Lato, sans-serif' },
  { name: 'Montserrat', value: 'Montserrat, sans-serif' },
  { name: 'Poppins', value: 'Poppins, sans-serif' },
  { name: 'Raleway', value: 'Raleway, sans-serif' },
  { name: 'Nunito', value: 'Nunito, sans-serif' },
  { name: 'Ubuntu', value: 'Ubuntu, sans-serif' },
  { name: 'Work Sans', value: '"Work Sans", sans-serif' },
  { name: 'Quicksand', value: 'Quicksand, sans-serif' },
  { name: 'Josefin Sans', value: '"Josefin Sans", sans-serif' },
  { name: 'Barlow', value: 'Barlow, sans-serif' },
  { name: 'DM Sans', value: '"DM Sans", sans-serif' },
  { name: 'Rubik', value: 'Rubik, sans-serif' },
  { name: 'Karla', value: 'Karla, sans-serif' },
  { name: 'Source Sans 3', value: '"Source Sans 3", sans-serif' },
  
  // System Sans-Serif
  { name: 'Arial', value: 'Arial, sans-serif' },
  { name: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { name: 'Verdana', value: 'Verdana, sans-serif' },
  
  // Serif Fonts
  { name: 'Playfair Display', value: '"Playfair Display", serif' },
  { name: 'Merriweather', value: 'Merriweather, serif' },
  { name: 'Lora', value: 'Lora, serif' },
  { name: 'Crimson Text', value: '"Crimson Text", serif' },
  { name: 'Libre Baskerville', value: '"Libre Baskerville", serif' },
  { name: 'EB Garamond', value: '"EB Garamond", serif' },
  { name: 'Cormorant', value: 'Cormorant, serif' },
  { name: 'Spectral', value: 'Spectral, serif' },
  { name: 'Bitter', value: 'Bitter, serif' },
  { name: 'Cardo', value: 'Cardo, serif' },
  
  // System Serif
  { name: 'Georgia', value: 'Georgia, serif' },
  { name: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  
  // Display & Decorative
  { name: 'Bebas Neue', value: '"Bebas Neue", sans-serif' },
  { name: 'Abril Fatface', value: '"Abril Fatface", serif' },
  { name: 'Righteous', value: 'Righteous, sans-serif' },
  { name: 'Anton', value: 'Anton, sans-serif' },
  { name: 'Oswald', value: 'Oswald, sans-serif' },
  { name: 'Fjalla One', value: '"Fjalla One", sans-serif' },
  { name: 'Archivo Black', value: '"Archivo Black", sans-serif' },
  { name: 'Bangers', value: 'Bangers, cursive' },
  
  // Script & Handwriting
  { name: 'Pacifico', value: 'Pacifico, cursive' },
  { name: 'Dancing Script', value: '"Dancing Script", cursive' },
  { name: 'Caveat', value: 'Caveat, cursive' },
  { name: 'Permanent Marker', value: '"Permanent Marker", cursive' },
  { name: 'Indie Flower', value: '"Indie Flower", cursive' },
  { name: 'Sacramento', value: 'Sacramento, cursive' },
  { name: 'Great Vibes', value: '"Great Vibes", cursive' },
  { name: 'Satisfy', value: 'Satisfy, cursive' },
  
  // Monospace
  { name: 'Roboto Mono', value: '"Roboto Mono", monospace' },
  { name: 'Source Code Pro', value: '"Source Code Pro", monospace' },
  { name: 'JetBrains Mono', value: '"JetBrains Mono", monospace' },
  { name: 'Fira Code', value: '"Fira Code", monospace' },
  { name: 'Space Mono', value: '"Space Mono", monospace' },
  { name: 'Courier New', value: '"Courier New", Courier, monospace' },
]

const FONT_SIZES = [10, 12, 14, 16, 18, 20, 22, 24, 28, 32, 36, 42, 48, 56, 64, 72]

interface RichTextEditorProps {
  content: any  // JSONContent
  onChange: (content: any) => void
  placeholder?: string
  minHeight?: string
  storeBodyFont?: string | null
  storeHeadingFont?: string | null
}

export function RichTextEditor({ content, onChange, placeholder, minHeight = '100px', storeBodyFont, storeHeadingFont }: RichTextEditorProps) {
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [showFontPicker, setShowFontPicker] = useState(false)
  const [currentColor, setCurrentColor] = useState('#000000')
  const [currentFontFamily, setCurrentFontFamily] = useState('Inter, sans-serif')
  const [currentFontSize, setCurrentFontSize] = useState('16')
  
  const fontPickerRef = useRef<HTMLDivElement>(null)
  const colorPickerRef = useRef<HTMLDivElement>(null)
  
  // Build store fonts list if available
  const storeFonts = []
  if (storeBodyFont && !FONT_FAMILIES.some(f => f.value.includes(storeBodyFont))) {
    storeFonts.push({ name: `${storeBodyFont} (Store Body)`, value: storeBodyFont })
  }
  if (storeHeadingFont && storeHeadingFont !== storeBodyFont && !FONT_FAMILIES.some(f => f.value.includes(storeHeadingFont))) {
    storeFonts.push({ name: `${storeHeadingFont} (Store Heading)`, value: storeHeadingFont })
  }

  // Close font picker and color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (fontPickerRef.current && !fontPickerRef.current.contains(event.target as Node)) {
        setShowFontPicker(false)
      }
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setShowColorPicker(false)
      }
    }

    if (showFontPicker || showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showFontPicker, showColorPicker])

  const editor = useEditor({
    extensions: [
      StarterKit,
      Color,
      TextStyle,
      FontFamily,
      FontSize,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getJSON())
    },
    onSelectionUpdate: ({ editor }) => {
      // Update toolbar state when cursor/selection changes
      const attrs = editor.getAttributes('textStyle')
      setCurrentColor(attrs.color || '#000000')
      setCurrentFontFamily(attrs.fontFamily || 'Inter, sans-serif')
      setCurrentFontSize(attrs.fontSize || '16')
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none px-3 py-2 rounded-b-lg',
        style: `min-height: ${minHeight}`,
      },
    },
    immediatelyRender: false, // Prevent SSR hydration mismatch
  })

  if (!editor) return null

  return (
    <>
      <FontLoader />
      <div className="border border-gray-300 rounded-lg bg-white">
        {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        {/* Font Family - Custom Dropdown */}
        <div className="relative" ref={fontPickerRef}>
          <button
            onClick={() => setShowFontPicker(!showFontPicker)}
            className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[140px] text-left flex items-center justify-between"
            title="Font Family"
            type="button"
          >
            <span>{FONT_FAMILIES.find(f => f.value === currentFontFamily)?.name || 'Inter'}</span>
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {showFontPicker && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto w-64">
              {/* Store Fonts Section */}
              {storeFonts.length > 0 && (
                <>
                  <div className="px-3 py-2 bg-indigo-50 border-b border-indigo-100">
                    <p className="text-xs font-semibold text-indigo-900 uppercase tracking-wide">
                      🏪 Your Store Fonts
                    </p>
                  </div>
                  {storeFonts.map(font => (
                    <button
                      key={font.value}
                      onClick={() => {
                        editor.chain().focus().setFontFamily(font.value).run()
                        setCurrentFontFamily(font.value)
                        setShowFontPicker(false)
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 transition-colors ${
                        currentFontFamily === font.value ? 'bg-indigo-100 text-indigo-700 font-medium' : ''
                      }`}
                      style={{ fontFamily: font.value }}
                      type="button"
                    >
                      {font.name}
                    </button>
                  ))}
                  <div className="border-t border-gray-200 my-1"></div>
                </>
              )}
              
              {/* All Fonts Section */}
              {storeFonts.length > 0 && (
                <div className="px-3 py-1.5 bg-gray-50">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    All Fonts
                  </p>
                </div>
              )}
              {FONT_FAMILIES.map(font => (
                <button
                  key={font.value}
                  onClick={() => {
                    editor.chain().focus().setFontFamily(font.value).run()
                    setCurrentFontFamily(font.value)
                    setShowFontPicker(false)
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 transition-colors ${
                    currentFontFamily === font.value ? 'bg-indigo-100 text-indigo-700' : ''
                  }`}
                  style={{ fontFamily: font.value }}
                  type="button"
                >
                  {font.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Font Size */}
        <select
          value={currentFontSize}
          onChange={(e) => {
            if (e.target.value === '') {
              (editor.chain().focus() as any).unsetFontSize().run()
            } else {
              (editor.chain().focus() as any).setFontSize(e.target.value).run()
            }
            setCurrentFontSize(e.target.value)
          }}
          className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          title="Font Size"
        >
          {FONT_SIZES.map(size => (
            <option key={size} value={size}>
              {size}px
            </option>
          ))}
        </select>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Bold */}
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('bold') ? 'bg-indigo-100 text-indigo-700' : ''
          }`}
          title="Bold (Ctrl+B)"
          type="button"
        >
          <Bold className="w-4 h-4" />
        </button>

        {/* Italic */}
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('italic') ? 'bg-indigo-100 text-indigo-700' : ''
          }`}
          title="Italic (Ctrl+I)"
          type="button"
        >
          <Italic className="w-4 h-4" />
        </button>

        {/* Underline */}
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive('underline') ? 'bg-indigo-100 text-indigo-700' : ''
          }`}
          title="Underline (Ctrl+U)"
          type="button"
        >
          <UnderlineIcon className="w-4 h-4" />
        </button>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Text Color */}
        <div className="relative" ref={colorPickerRef}>
          <button
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="flex items-center gap-1 p-2 rounded hover:bg-gray-200 transition-colors"
            title="Text Color"
            type="button"
          >
            <Palette className="w-4 h-4" />
            <div 
              className="w-4 h-4 border border-gray-300 rounded"
              style={{ backgroundColor: currentColor }}
            />
          </button>
          
          {showColorPicker && (
            <div className="absolute top-full left-0 mt-1 p-3 bg-white border border-gray-300 rounded-lg shadow-lg z-50 w-64">
              <div className="mb-3">
                <input
                  type="color"
                  value={currentColor}
                  onChange={(e) => {
                    setCurrentColor(e.target.value)
                    editor.chain().focus().setColor(e.target.value).run()
                  }}
                  className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                  title="Pick color"
                />
              </div>
              
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Hex Code
                </label>
                <input
                  type="text"
                  value={currentColor}
                  onChange={(e) => {
                    const value = e.target.value
                    setCurrentColor(value)
                    if (/^#[0-9A-F]{6}$/i.test(value)) {
                      editor.chain().focus().setColor(value).run()
                    }
                  }}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded font-mono"
                  placeholder="#000000"
                  maxLength={7}
                />
              </div>
              
              {/* Color Presets */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Quick Colors
                </label>
                <div className="grid grid-cols-8 gap-1">
                  {[
                    '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
                    '#800000', '#808080', '#800080', '#008000', '#808000', '#000080', '#008080', '#C0C0C0',
                  ].map(color => (
                    <button
                      key={color}
                      onClick={() => {
                        setCurrentColor(color)
                        editor.chain().focus().setColor(color).run()
                      }}
                      className="w-6 h-6 border border-gray-300 rounded hover:scale-110 transition-transform"
                      style={{ backgroundColor: color }}
                      title={color}
                      type="button"
                    />
                  ))}
                </div>
              </div>
              
              <button
                onClick={() => {
                  editor.chain().focus().unsetColor().run()
                  setShowColorPicker(false)
                }}
                className="mt-2 w-full px-2 py-1 text-xs text-gray-600 hover:text-gray-800 border border-gray-300 rounded"
                type="button"
              >
                Reset Color
              </button>
            </div>
          )}
        </div>

        <div className="w-px h-6 bg-gray-300 mx-1" />

        {/* Alignment */}
        <button
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive({ textAlign: 'left' }) ? 'bg-indigo-100 text-indigo-700' : ''
          }`}
          title="Align Left"
          type="button"
        >
          <AlignLeft className="w-4 h-4" />
        </button>

        <button
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive({ textAlign: 'center' }) ? 'bg-indigo-100 text-indigo-700' : ''
          }`}
          title="Align Center"
          type="button"
        >
          <AlignCenter className="w-4 h-4" />
        </button>

        <button
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`p-2 rounded hover:bg-gray-200 transition-colors ${
            editor.isActive({ textAlign: 'right' }) ? 'bg-indigo-100 text-indigo-700' : ''
          }`}
          title="Align Right"
          type="button"
        >
          <AlignRight className="w-4 h-4" />
        </button>
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} />
      </div>
    </>
  )
}

